import React from 'react';
import { 
  Descriptions, 
  Tag, 
  Avatar, 
  Space, 
  Typography, 
  Card,
  Row,
  Col,
  Divider
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined,
  BankOutlined,
  CalendarOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { Employee } from '../../types/employee';

const { Title, Text } = Typography;

interface EmployeeProfileProps {
  employee: Employee;
}

const EmployeeProfile: React.FC<EmployeeProfileProps> = ({ employee }) => {
  const formatCurrency = (amount?: string) => {
    return amount && amount !== '0.00' ? `₦${parseFloat(amount).toLocaleString()}` : 'Not specified';
  };

  const formatDate = (date?: string) => {
    return date ? new Date(date).toLocaleDateString() : 'Not specified';
  };

  return (
    <div>
      {/* Header Section */}
      <Card style={{ marginBottom: '16px' }}>
        <Row align="middle" gutter={16}>
          <Col>
            <Avatar 
              size={80} 
              icon={<UserOutlined />}
              style={{ backgroundColor: '#1890ff' }}
            >
              {employee.first_name[0]}{employee.last_name[0]}
            </Avatar>
          </Col>
          <Col flex="auto">
            <Title level={3} style={{ margin: 0 }}>
              {employee.first_name} {employee.last_name}
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              {employee.position} • {employee.department}
            </Text>
            <br />
            <Space style={{ marginTop: '8px' }}>
              <Tag color="blue">{employee.employee_id}</Tag>
              <Tag color={employee.status === 'active' ? 'green' : 'red'}>
                {employee.status.toUpperCase()}
              </Tag>
              <Tag color="orange">
                {employee.employment_type === 'full_time' ? 'Full Time' : 'Part Time'}
              </Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Personal Information */}
      <Card title="Personal Information" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Email" span={2}>
            <Space>
              <MailOutlined style={{ color: '#1890ff' }} />
              {employee.email}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            <Space>
              <PhoneOutlined style={{ color: '#52c41a' }} />
              {employee.phone || 'Not provided'}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Date of Birth">
            <Space>
              <CalendarOutlined />
              {formatDate(employee.date_of_birth)}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Gender">
            {employee.gender || 'Not specified'}
          </Descriptions.Item>
          <Descriptions.Item label="Marital Status">
            {employee.marital_status || 'Not specified'}
          </Descriptions.Item>
          <Descriptions.Item label="Nationality">
            {employee.nationality || 'Not specified'}
          </Descriptions.Item>
          <Descriptions.Item label="Address" span={2}>
            <Space>
              <HomeOutlined />
              {employee.address || 'Not provided'}
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Emergency Contact */}
      <Card title="Emergency Contact" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Contact Name">
            {employee.emergency_contact_name || 'Not provided'}
          </Descriptions.Item>
          <Descriptions.Item label="Contact Phone">
            {employee.emergency_contact_phone || 'Not provided'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Employment Details */}
      <Card title="Employment Details" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Employee ID">
            {employee.employee_id}
          </Descriptions.Item>
          <Descriptions.Item label="Department">
            <Tag color="blue">{employee.department}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Position">
            {employee.position}
          </Descriptions.Item>
          <Descriptions.Item label="Role">
            <Tag color="purple">{employee.role}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Hire Date">
            {formatDate(employee.hire_date)}
          </Descriptions.Item>
          <Descriptions.Item label="Employment Type">
            <Tag color={employee.employment_type === 'full_time' ? 'green' : 'orange'}>
              {employee.employment_type === 'full_time' ? 'Full Time' : 'Part Time'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Work Location">
            {employee.work_location || 'Not specified'}
          </Descriptions.Item>
          <Descriptions.Item label="Reporting Manager">
            {employee.reporting_manager || 'Not assigned'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Compensation */}
      <Card title="Compensation & Banking" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Basic Salary">
            {formatCurrency(employee.basic_salary)}
          </Descriptions.Item>
          <Descriptions.Item label="Allowances">
            {formatCurrency(employee.allowances)}
          </Descriptions.Item>
          <Descriptions.Item label="Bank Name">
            <Space>
              <BankOutlined />
              {employee.bank_name || 'Not provided'}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Account Number">
            {employee.account_number || 'Not provided'}
          </Descriptions.Item>
          <Descriptions.Item label="Account Name" span={2}>
            {employee.account_name || 'Not provided'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Performance */}
      <Card title="Performance & Reviews" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Performance Rating">
            {employee.performance_rating ? `${employee.performance_rating}/5.0` : 'Not rated'}
          </Descriptions.Item>
          <Descriptions.Item label="Last Review Date">
            {formatDate(employee.last_review_date)}
          </Descriptions.Item>
          <Descriptions.Item label="Next Review Date" span={2}>
            {formatDate(employee.next_review_date)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Notes */}
      {employee.notes && (
        <Card title="Additional Notes">
          <Text>{employee.notes}</Text>
        </Card>
      )}

      <Divider />
      <Text type="secondary" style={{ fontSize: '12px' }}>
        Created: {formatDate(employee.created_at)} | 
        Last Updated: {formatDate(employee.updated_at)}
      </Text>
    </div>
  );
};

export default EmployeeProfile;
